// src/app/shared/components/dashboard/dashboard-catalog-widget-form-dialog.component.ts
import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  WidgetType,
  WidgetPlatform,
} from '../../../core/enums/dashboard.enum';
import {
  DashboardLayoutService,
  CreateWidgetDto,
  WidgetPlatformConfig,
  WidgetCatalogEntry,
} from '../../../core/services/dashboard-layout.service';
import { finalize } from 'rxjs/operators';

export interface DashboardCatalogWidgetFormDialogData {
  pageId: number;
  widget?: WidgetCatalogEntry;
  platform?: WidgetPlatform | string;
}

@Component({
  standalone: true,
  selector: 'app-dashboard-catalog-widget-form-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
  ],
  template: `
    <h2 mat-dialog-title class="text-xl font-semibold !text-slate-900 dark:!text-slate-100">
      {{ isEditMode ? 'Edit Platform Configuration' : 'Create New Widget' }}
    </h2>
    <mat-dialog-content class="min-w-[320px] sm:min-w-[500px] py-4">
      <form [formGroup]="form" class="flex flex-col gap-4">
        @if (!isEditMode) {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Widget Name</mat-label>
              <input matInput formControlName="name" placeholder="Total Sales" />
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Widget Key</mat-label>
              <input matInput formControlName="widget_key" placeholder="total_sales_kpi" />
            </mat-form-field>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Type</mat-label>
              <mat-select formControlName="type">
                @for (t of widgetTypes; track t) {
                  <mat-option [value]="t">{{ t | titlecase }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Category</mat-label>
              <input matInput formControlName="category" placeholder="sales" />
            </mat-form-field>
          </div>

          <div class="flex items-center gap-4 py-2">
            <mat-checkbox formControlName="is_default">Is Default</mat-checkbox>
            <mat-form-field appearance="outline" class="w-24">
              <mat-label>Position</mat-label>
              <input matInput type="number" formControlName="default_position" />
            </mat-form-field>
          </div>
        }

        <div class="border-t border-slate-200 dark:border-slate-700 mt-2 pt-4">
          <div class="text-sm font-medium mb-3 text-slate-700 dark:text-slate-300">
            Platform Configuration{{ isEditMode ? ' (' + data.platform + ')' : 's' }}
          </div>
          
          <div formArrayName="platform_configs" class="space-y-6">
            @for (config of platformConfigs.controls; track $index; let i = $index) {
              <div [formGroupName]="i" class="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div class="font-medium text-xs uppercase tracking-wider text-slate-500 mb-4">
                  {{ config.get('platform')?.value }}
                </div>
                
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <mat-form-field appearance="outline" class="w-full col-span-1 sm:col-span-2">
                    <mat-label>Component Name</mat-label>
                    <input matInput formControlName="component_name" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Default Columns</mat-label>
                    <input matInput type="number" formControlName="default_cols" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Default Rows</mat-label>
                    <input matInput type="number" formControlName="default_rows" />
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Min Columns</mat-label>
                    <input matInput type="number" formControlName="min_cols" />
                  </mat-form-field>

                  <div class="flex items-center">
                    <mat-checkbox formControlName="is_available">Available on Platform</mat-checkbox>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="!bg-white dark:!bg-slate-900 !border-t !border-slate-200 dark:!border-slate-700">
      <button mat-button [disabled]="loading" (click)="onCancel()">Cancel</button>
      <button 
        mat-flat-button 
        color="primary" 
        [disabled]="form.invalid || loading" 
        (click)="onSubmit()"
      >
        {{ loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create') }}
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class DashboardCatalogWidgetFormDialogComponent implements OnInit {
  data = inject<DashboardCatalogWidgetFormDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject(MatDialogRef<DashboardCatalogWidgetFormDialogComponent>);
  private fb = inject(FormBuilder);
  private layoutService = inject(DashboardLayoutService);

  form!: FormGroup;
  loading = false;
  isEditMode = false;
  widgetTypes = Object.values(WidgetType);
  platforms = Object.values(WidgetPlatform);

  ngOnInit() {
    this.isEditMode = !!(this.data.widget && this.data.platform);
    this.initForm();
  }

  private initForm() {
    if (this.isEditMode && this.data.widget) {
      // Find existing config for platform if it exists
      const existingConfig = this.data.widget.platform_configs?.find(
        c => c.platform === this.data.platform
      ) || this.data.widget; // Fallback to flattened fields if platform_configs missing

      this.form = this.fb.group({
        platform_configs: this.fb.array([
          this.createPlatformConfigGroup(this.data.platform as WidgetPlatform, existingConfig as any)
        ])
      });
    } else {
      this.form = this.fb.group({
        name: ['', Validators.required],
        widget_key: ['', Validators.required],
        type: [WidgetType.KPI, Validators.required],
        category: ['common', Validators.required],
        is_default: [false],
        default_position: [0],
        metadata: [{}],
        platform_configs: this.fb.array(
          this.platforms.map(p => this.createPlatformConfigGroup(p))
        )
      });
    }
  }

  private createPlatformConfigGroup(platform: WidgetPlatform, existing?: any): FormGroup {
    return this.fb.group({
      platform: [platform],
      component_name: [existing?.component_name || '', Validators.required],
      default_cols: [existing?.default_cols || (platform === WidgetPlatform.MOBILE ? 12 : 6), Validators.required],
      default_rows: [existing?.default_rows || 1],
      min_cols: [existing?.min_cols || (platform === WidgetPlatform.MOBILE ? 6 : 3)],
      is_available: [existing?.is_available ?? true],
      platform_metadata: [existing?.platform_metadata || {}]
    });
  }

  get platformConfigs() {
    return this.form.get('platform_configs') as FormArray;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSubmit() {
    if (this.form.invalid) return;

    this.loading = true;
    const value = this.form.value;

    if (this.isEditMode && this.data.widget && this.data.platform) {
      const config = value.platform_configs[0];
      this.layoutService.patchWidgetPlatformConfig(
        this.data.widget.id,
        this.data.platform as string,
        config
      ).pipe(finalize(() => this.loading = false))
       .subscribe(() => this.dialogRef.close(true));
    } else {
      this.layoutService.createCatalogWidget(this.data.pageId, value as CreateWidgetDto)
        .pipe(finalize(() => this.loading = false))
        .subscribe(() => this.dialogRef.close(true));
    }
  }
}
